/** Reine Logik von Schritt 3 „KI-Bewertung“. */

import { criteriaEqual, sameName } from '../../campaign-wizard.logic';
import { CriteriaSet, CriteriaTemplate } from '../../campaign-wizard.types';

export type CriteriaColumn = keyof CriteriaSet;
export const CRITERIA_COLUMNS: CriteriaColumn[] = ['must', 'nice', 'excl'];

export const cloneCriteria = (c: CriteriaSet): CriteriaSet => ({
  must: [...c.must],
  nice: [...c.nice],
  excl: [...c.excl],
});

/** Kriterium zur Spalte hinzufügen (getrimmt; leer und doppelt ändern nichts). */
export const addCriterion = (
  set: CriteriaSet,
  column: CriteriaColumn,
  text: string
): CriteriaSet => {
  const value = text.trim();
  if (!value || set[column].some(c => sameName(c, value))) return set;
  return { ...set, [column]: [...set[column], value] };
};

export const removeCriterion = (
  set: CriteriaSet,
  column: CriteriaColumn,
  index: number
): CriteriaSet => ({
  ...set,
  [column]: set[column].filter((_, i) => i !== index),
});

/** „angepasst“: Kriterien weichen von der Vorlage ab. Ohne Vorlage (z. B. aus dem Briefing) nie. */
export const isModified = (current: CriteriaSet, base: CriteriaSet | null | undefined): boolean =>
  base ? !criteriaEqual(current, base) : false;

export const isEmptyCriteria = (c: CriteriaSet): boolean =>
  c.must.length + c.nice.length + c.excl.length === 0;

/** Vorlage, die zu wählen ist, wenn noch nichts gewählt ist (erste Vorlage, nur bei leeren Kriterien). */
export const defaultTemplate = (
  templates: CriteriaTemplate[],
  current: { templateId: string; judging: CriteriaSet }
): CriteriaTemplate | null =>
  templates.length > 0 && !current.templateId && isEmptyCriteria(current.judging)
    ? templates[0]
    : null;

/** Vorlage zum aktuellen Zustand finden (ID, sonst Name). */
export const findTemplate = (
  templates: CriteriaTemplate[],
  current: { templateId: string; templateName: string }
): CriteriaTemplate | undefined =>
  templates.find(t => t.id === current.templateId) ??
  (current.templateName ? templates.find(t => sameName(t.name, current.templateName)) : undefined);

/** Gibt es schon eine Vorlage mit diesem Namen (ohne Groß-/Kleinschreibung, getrimmt)? Sie würde überschrieben. */
export const findSameName = (
  templates: CriteriaTemplate[],
  name: string
): CriteriaTemplate | undefined => {
  const n = name.trim();
  return n ? templates.find(t => sameName(t.name, n)) : undefined;
};

/** Vorschlag für den Namen beim Speichern: unverändert der Vorlagenname, angepasst mit Zusatz. */
export const suggestTemplateName = (
  templateName: string,
  modified: boolean,
  suffix: string
): string => (templateName ? (modified ? `${templateName} (${suffix})` : templateName) : '');

/** Namensvorschlag im Speichern-Dialog: Vorlagenname, sonst Jobtitel, sonst Kampagnenname. */
export const defaultSaveName = (input: {
  templateName: string;
  modified: boolean;
  suffix: string;
  jobTitle?: string;
  campaignName?: string;
}): string =>
  suggestTemplateName(input.templateName, input.modified, input.suffix) ||
  (input.jobTitle ?? '').trim() ||
  (input.campaignName ?? '').trim();

/** Längster Vorlagenname — identisch zum Server (`CreateCriteriaTemplateBodySchema`). */
export const TEMPLATE_NAME_MAX = 100;
